import React, { useCallback, useEffect, useRef, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Container from '@material-ui/core/Container';
import Link from '@material-ui/core/Link';

import VariableTextField from '../../components/variableTextField';
import VariableSelector from '../../components/variableSelector';
import { useAPI } from '../../utils/api';
import CombineSelector from '../../components/combineSelector';
import { Checkbox, FormControlLabel } from '@material-ui/core';

import { MODE, validMode } from '../../utils/mode';
import { useAuth } from '../../utils/auth';

const parseContact = (formData) => {
  if (!formData) return;

  const serviceAreaID = formData.get('serviceArea');
  const districtID = formData.get('district');
  const contactPerson = formData.get('contactPerson');
  const company = formData.get('company');
  const department = formData.get('department');
  const isEmployee = formData.get('isEmployee') === 'true';
  const phoneNumber = formData.get('phoneNumber');
  const secondPhoneNumber = formData.get('secondPhoneNumber');
  const address = formData.get('address');
  const note = formData.get('note');
  const email = formData.get('email');

  const parsedData = {
    contactPerson,
    company,
    department,
    isEmployee,
    phoneNumber,
    secondPhoneNumber,
    districtID,
    address,
    serviceAreaID,
    note,
    email,
  };

  return parsedData;
};

const ContactDetailPage = () => {
  const api = useAPI();
  const auth = useAuth();
  const history = useHistory();

  const { mode, contactID } = useParams();

  const contactForm = useRef();

  const [isLoaded, setIsLoaded] = useState(false);
  const [isUnmounted, setIsUnmounted] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [shouldPageUpdated, setShouldPageUpdated] = useState(false);

  const [cityList, setCityList] = useState([]);
  const [serviceAreaList, setServiceAreaList] = useState([]);

  const [contact, setContact] = useState({});
  const [defaultMemberId, setDefaultMemberId] = useState('');

  const submit = (event) => {
    event.preventDefault();

    if (mode === MODE.VIEW) {
      openEditMode();
      return;
    }

    const formData = new FormData(event.currentTarget);
    const data = parseContact(formData);

    if (mode === MODE.EDIT) {
      data.contactID = contactID;

      api
        .updateContactDetail(data)
        .then(() => {
          fetchContactDetail();
          openViewMode();
        })
        .catch(() => {});
    } else if (mode === MODE.NEW) {
      data.memberID = contactID;

      api
        .submitCreateNewContact(data)
        .then((isSuccess) => {
          if (isSuccess) {
            history.replace(`/contact/list/${data.memberID}`);
          }
        })
        .catch(() => {});
    }
  };

  // eslint-disable-next-line no-unused-vars
  const openEditMode = () => {
    const newPath = generatePath(`/contact/${MODE.EDIT}/${contactID || ''}`);
    history.replace({ pathname: newPath });
  };

  const openViewMode = () => {
    const newPath = generatePath(`/contact/${MODE.VIEW}/${contactID || ''}`);
    history.replace({ pathname: newPath });
  };

  const fetchContactDetail = useCallback(() => {
    api
      .getContactDetail({ contactID })
      .then((contactData) => {
        setShouldPageUpdated(true);
        setContact(contactData);
        setDefaultMemberId(contactData.defaultMemberID);
      })
      .catch(() => {});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactID]);

  useEffect(() => {
    if (!validMode(mode)) {
      history.replace(`/contact/list/${auth.user.memberID}`);
    }

    setShouldPageUpdated(true);
  }, [mode, history, auth.user.memberID]);

  useEffect(() => {
    if (isUnmounted) return;

    const getCitys = api
      .getDropdownCitys()
      .then((result) => {
        if (!isUnmounted) {
          setCityList(result);
        }
      })
      .catch(() => {});

    const getServiceAreas = api
      .getServiceAreaList()
      .then((result) => {
        if (!isUnmounted) {
          setServiceAreaList(result);
        }
      })
      .catch(() => {});

    Promise.all([getCitys, getServiceAreas]).then(() => {
      setIsLoaded(true);
    });

    return () => {
      setIsUnmounted(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldPageUpdated && mode === MODE.NEW) {
      setContact({});
    }

    return () => {
      setShouldPageUpdated(false);
    };
  }, [mode, shouldPageUpdated]);

  useEffect(() => {
    if (isMounted) return;

    if (mode === MODE.VIEW || mode === MODE.EDIT) {
      if (contactID && contactID !== 'undefined' && isLoaded) {
        fetchContactDetail();
        setIsMounted(true);
      }
    } else if (mode === MODE.NEW) setIsMounted(true);
  }, [fetchContactDetail, isMounted, contactID, mode, isLoaded]);

  return (
    <>
      {!shouldPageUpdated && (mode === MODE.NEW || contact) ? (
        <>
          <Box ref={contactForm} component="form" noValidate onSubmit={submit}>
            <Paper
              elevation={4}
              sx={{
                paddingTop: { xs: 1, md: 2 },
                paddingBottom: { xs: 2, md: 4 },
                marginBottom: 4,
                maxWidth: '900px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <Container>
                <Typography variant="h6" marginBottom={4}>
                  聯絡人
                </Typography>

                <Grid container spacing={{ xs: 1, md: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="contactPerson"
                      label="聯絡人"
                      text={contact.name}
                      required={true}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="company"
                      label="公司"
                      text={contact.company}
                      required={false}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="department"
                      label="部門"
                      text={contact.department}
                      required={false}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={
                      mode === MODE.VIEW
                        ? {}
                        : { display: 'flex', alignItems: 'center' }
                    }
                  >
                    {mode === MODE.VIEW ? (
                      <VariableTextField
                        mode={MODE.VIEW}
                        name="isEmployeeForDisplay"
                        label="雇員"
                        text={contact.isEmployee ? '是' : '否'}
                        required={false}
                      />
                    ) : (
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="isEmployee"
                            value="true"
                            color="primary"
                            defaultChecked={contact.isEmployee}
                          />
                        }
                        label="雇員"
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="secondPhoneNumber"
                      label="公司電話"
                      text={contact.secondPhone}
                      required={true}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="phoneNumber"
                      label="手機電話"
                      text={contact.phone}
                      required={true}
                    />
                  </Grid>

                  <CombineSelector
                    mode={mode}
                    optionsList={{
                      firstName: 'city',
                      firstLabel: '縣市',
                      options: cityList,
                      secondName: 'district',
                      secondLabel: '區域',
                      firstText: contact.cityName,
                      secondText: contact.districtName,
                      firstValue: contact.cityName,
                      secondValue: contact.districtID,
                    }}
                    secondOptionsKeyName="districts"
                  />

                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="address"
                      label="地址"
                      text={contact.streetAddress}
                      required={true}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VariableSelector
                      mode={mode}
                      name="serviceArea"
                      label="服務地區"
                      defaultValue={contact.serviceAreaID}
                      options={serviceAreaList}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="note"
                      label="備註"
                      text={contact.note}
                      required={false}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="email"
                      label="電子郵件"
                      text={contact.email}
                      required={false}
                    />
                  </Grid>
                </Grid>
              </Container>
            </Paper>

            <Grid
              container
              spacing={3}
              sx={{ margin: 'auto', width: '100%', justifyContent: 'center' }}
            >
              <Grid item xs={10} sm={4} sx={{ maxWidth: '200px' }}>
                {auth && auth.user && auth.user.memberID ? (
                  <Link
                    underline="none"
                    href={`/#/contact/list/${
                      mode === MODE.NEW ? contactID : defaultMemberId
                    }`}
                  >
                    <Button
                      type="button"
                      fullWidth
                      variant="contained"
                      color="primary"
                    >
                      返回常用聯絡人
                    </Button>
                  </Link>
                ) : undefined}
              </Grid>
              {mode !== MODE.VIEW ? (
                <Grid item xs={10} sm={4} sx={{ maxWidth: '200px' }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="success"
                    sx={{ color: 'white' }}
                  >
                    {mode === MODE.NEW ? '新增' : '儲存'}
                  </Button>
                </Grid>
              ) : (
                <Grid item xs={10} sm={4} sx={{ maxWidth: '200px' }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                  >
                    編輯
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        </>
      ) : undefined}
    </>
  );
};

export default ContactDetailPage;
